import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileStack,
  Loader2,
  Mic,
  Scale,
  Search,
  Sparkles,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { buildSentinelMatterContext, getMatterDeskRoute, resolveMatterDesk } from '@/lib/matterDesk';
import {
  AppEmptyState,
  AppMetricCard,
  AppPage,
  AppPageHeader,
  AppSectionHeader,
} from '@/components/layout/AppPage';
import FeatureHistoryMenu from '@/components/FeatureHistoryMenu';
import HearingPrepWorkspace from '@/components/HearingPrepWorkspace';
import JollyDictationButton from '@/components/JollyDictationButton';
import MatterWorkspaceContext, { withQueryParams } from '@/components/matter/MatterWorkspaceContext';

const CASE_MODES = [
  { value: 'new', label: 'New matter', description: 'Open a fresh matter from your facts, notes, and live objective.' },
  { value: 'previous', label: 'Previous matter', description: 'Resume an existing matter with continuity already intact.' },
  { value: 'indiankanoon', label: 'Import from Indian Kanoon', description: 'Anchor the matter in verified Indian Kanoon authority from the start.' },
  { value: 'hypothetical', label: 'Hypothetical matter', description: 'Start from a simulated dispute without cluttering the rest of the product.' },
];

function normalizePlan(value) {
  const candidate = String(value || 'FREE').trim().toUpperCase();
  if (candidate === 'EARLY_BIRD' || candidate === 'EARLY_BELIEVER') return 'COMMUNITY';
  return candidate;
}

function createdJudgeLimit(user) {
  const platformRole = String(user?.platform_role || '').toUpperCase();
  if (platformRole === 'ADMIN' || platformRole === 'OWNER') return 999;
  const plan = normalizePlan(user?.plan);
  if (plan === 'COMMUNITY') return 5;
  if (['PRO', 'TEAM', 'FIRM'].includes(plan)) return 999;
  return 3;
}

function judgeLimitLabel(limit) {
  return limit >= 999 ? 'Unlimited' : String(limit);
}

function trimText(value, max = 220) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}…`;
}

function buildEmptyForm() {
  return {
    title: '',
    description: '',
    summary: '',
    current_objective: '',
    court_forum: '',
    case_number: '',
    source_query: '',
    judge_prefill_text: '',
  };
}

function normalizeImportedCase(item) {
  if (!item) return null;
  return {
    tid: item.tid || item.url || item.title || '',
    title: item.title || '',
    headline: item.headline || '',
    docsource: item.docsource || '',
    url: item.url || '',
    publishdate: item.publishdate || '',
    provenance: item.provenance || [],
    judgeInference: item.judgeInference || null,
  };
}

function buildMatterPayload({ mode, form, importedCase, matterId, workspaceId, judges }) {
  return {
    mode,
    title: form.title || '',
    description: form.description || '',
    summary: form.summary || '',
    current_objective: form.current_objective || '',
    matter_id: matterId || '',
    workspace_id: workspaceId || '',
    court_forum: form.court_forum || '',
    case_number: form.case_number || '',
    source_query: form.source_query || '',
    judge_prefill_text: form.judge_prefill_text || '',
    imported_case: normalizeImportedCase(importedCase),
    judges: judges || [],
  };
}

function labelTone(provenance) {
  if (provenance?.verified) {
    return {
      background: 'rgba(34,197,94,0.12)',
      border: '1px solid rgba(34,197,94,0.26)',
      color: '#15803d',
    };
  }
  if (provenance?.type === 'inferred') {
    return {
      background: 'rgba(245,158,11,0.12)',
      border: '1px solid rgba(245,158,11,0.28)',
      color: '#b45309',
    };
  }
  return {
    background: 'rgba(148,163,184,0.12)',
    border: '1px solid var(--border-default)',
    color: 'var(--text-secondary)',
  };
}

function ProvenanceChip({ item }) {
  const tone = labelTone(item);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '7px 12px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.03em',
        ...tone,
      }}
    >
      {item.label}
    </span>
  );
}

function ModeCard({ mode, active, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(mode.value)}
      style={{
        textAlign: 'left',
        borderRadius: '20px',
        border: active ? '1px solid rgba(15,118,110,0.38)' : '1px solid var(--border-default)',
        background: active ? 'linear-gradient(135deg, rgba(15,118,110,0.08), rgba(255,255,255,0.92))' : 'var(--bg-elevated)',
        padding: '18px',
        display: 'grid',
        gap: '10px',
        cursor: 'pointer',
      }}
    >
      <span style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
        Matter intake
      </span>
      <span style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: 800 }}>
        {mode.label}
      </span>
      <span style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.7 }}>
        {mode.description}
      </span>
    </button>
  );
}

function CasePage() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const { matterId = '' } = useParams();

  const [mode, setMode] = useState('new');
  const [loadingMatter, setLoadingMatter] = useState(Boolean(matterId));
  const [savingMatter, setSavingMatter] = useState(false);
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [form, setForm] = useState(buildEmptyForm);
  const [ikResults, setIkResults] = useState([]);
  const [searchingIK, setSearchingIK] = useState(false);
  const [selectedImportedCase, setSelectedImportedCase] = useState(null);
  const [matterWorkspace, setMatterWorkspace] = useState(null);
  const [hearingMode, setHearingMode] = useState('senior_advocate_prep');
  const [hearingPackRefreshing, setHearingPackRefreshing] = useState(false);

  const judgeLimit = useMemo(() => createdJudgeLimit(user), [user]);
  const judgeProfiles = matterWorkspace?.case?.judge_profiles || [];
  const matterDesk = useMemo(() => resolveMatterDesk(matterWorkspace), [matterWorkspace]);

  const applyWorkspacePayload = useCallback((payload) => {
    setMatterWorkspace(payload);
    setMode(payload?.case?.mode || 'new');
    setForm({
      title: payload?.case?.title || payload?.matter?.title || '',
      description: payload?.matter?.description || '',
      summary: payload?.case?.summary || '',
      current_objective: payload?.matterDesk?.current_objective?.text || payload?.matter?.case_current_objective || '',
      court_forum: payload?.matter?.court_forum || '',
      case_number: payload?.matter?.case_number || '',
      source_query: payload?.matter?.case_import?.source_query || '',
      judge_prefill_text: '',
    });
    setSelectedImportedCase(normalizeImportedCase(payload?.matter?.case_import));
    setHearingMode(payload?.hearingPack?.default_mode || 'senior_advocate_prep');
  }, []);

  const loadCases = useCallback(async () => {
    setLoadingCases(true);
    try {
      const response = await fetch(`${API}/cases`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const payload = response.ok ? await response.json() : [];
      setCases(Array.isArray(payload) ? payload : []);
    } catch {
      setCases([]);
    } finally {
      setLoadingCases(false);
    }
  }, [getAuthHeaders]);

  const loadCaseDetail = useCallback(async (nextMatterId) => {
    if (!nextMatterId) {
      setMatterWorkspace(null);
      setForm(buildEmptyForm());
      setSelectedImportedCase(null);
      return;
    }
    setLoadingMatter(true);
    try {
      const response = await fetch(`${API}/cases/${nextMatterId}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Unable to load matter strategy');
      applyWorkspacePayload(payload);
    } catch (error) {
      toast.error(error.message || 'Unable to load matter strategy');
    } finally {
      setLoadingMatter(false);
    }
  }, [applyWorkspacePayload, getAuthHeaders]);

  useEffect(() => {
    document.title = 'Matter — Advoq';
  }, []);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  useEffect(() => {
    loadCaseDetail(matterId);
  }, [loadCaseDetail, matterId]);

  useEffect(() => {
    if (!matterWorkspace?.matter?.matter_id) return undefined;
    window.sentinelMatterContext = buildSentinelMatterContext(resolveMatterDesk(matterWorkspace), matterWorkspace);
    return () => { delete window.sentinelMatterContext; };
  }, [matterWorkspace]);

  const saveMatter = useCallback(async ({ navigateToMatter = false } = {}) => {
    setSavingMatter(true);
    try {
      const payload = buildMatterPayload({
        mode,
        form,
        importedCase: selectedImportedCase,
        matterId: matterWorkspace?.matter?.matter_id || '',
        workspaceId: matterWorkspace?.case?.workspace_id || '',
        judges: matterWorkspace?.case?.judge_profiles || [],
      });
      const targetMatterId = payload.matter_id;
      const url = targetMatterId ? `${API}/cases/${targetMatterId}/save` : `${API}/cases`;
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const nextPayload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(nextPayload.detail || 'Unable to save matter');
      applyWorkspacePayload(nextPayload);
      loadCases();
      toast.success(targetMatterId ? 'Matter strategy saved.' : 'Matter created.');
      if (navigateToMatter && nextPayload?.matter?.matter_id) {
        navigate(`/case/${nextPayload.matter.matter_id}`);
      }
      return nextPayload;
    } catch (error) {
      toast.error(error.message || 'Unable to save matter');
      return null;
    } finally {
      setSavingMatter(false);
    }
  }, [applyWorkspacePayload, form, getAuthHeaders, loadCases, matterWorkspace?.case?.judge_profiles, matterWorkspace?.case?.workspace_id, matterWorkspace?.matter?.matter_id, mode, navigate, selectedImportedCase]);

  const runIndianKanoonSearch = async () => {
    const query = form.source_query.trim();
    if (!query) return;
    setSearchingIK(true);
    try {
      const response = await fetch(`${API}/cases/search/indiankanoon`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ query }),
      });
      const payload = response.ok ? await response.json() : { results: [] };
      setIkResults(payload.results || []);
    } catch {
      setIkResults([]);
    } finally {
      setSearchingIK(false);
    }
  };

  const selectImportedCase = (item) => {
    setSelectedImportedCase(item);
    setForm((prev) => ({
      ...prev,
      title: item.title || prev.title,
      description: trimText(item.headline, 320),
      summary: trimText(item.headline, 420),
      current_objective: prev.current_objective || trimText(item.headline || item.title || '', 200),
      court_forum: item.docsource || prev.court_forum,
    }));
  };

  const regenerateHearingPack = useCallback(async () => {
    if (!matterWorkspace?.matter?.matter_id) return;
    setHearingPackRefreshing(true);
    try {
      const response = await fetch(`${API}/matters/${matterWorkspace.matter.matter_id}/hearing-pack/regenerate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Unable to refresh hearing pack');
      setMatterWorkspace((current) => (
        current ? { ...current, hearingPack: payload } : current
      ));
      setHearingMode(payload?.default_mode || 'senior_advocate_prep');
      toast.success('Hearing pack refreshed');
    } catch (error) {
      toast.error(error.message || 'Unable to refresh hearing pack');
    } finally {
      setHearingPackRefreshing(false);
    }
  }, [getAuthHeaders, matterWorkspace?.matter?.matter_id]);

  const openRecentCase = async (item) => {
    navigate(`/case/${item.matter_id || item.id}`);
    return true;
  };

  const renderIntake = () => (
    <>
      <section style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {CASE_MODES.map((item) => (
          <ModeCard key={item.value} mode={item} active={mode === item.value} onClick={setMode} />
        ))}
      </section>

      <section className="advoq-card" style={{ padding: '24px', borderRadius: '24px', display: 'grid', gap: '18px' }}>
        <AppSectionHeader
          eyebrow="Matter intake"
          title={CASE_MODES.find((item) => item.value === mode)?.label || 'Open a matter'}
          description="Keep intake calm. Save the matter first, then add judge strategy only if this file genuinely needs it."
        />

        {mode === 'previous' ? (
          <div style={{ display: 'grid', gap: '12px' }}>
            {loadingCases ? (
              <div style={{ color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <Loader2 size={14} className="animate-spin" /> Loading previous matters...
              </div>
            ) : cases.length ? (
              cases.map((item) => (
                <button
                  key={item.matter_id}
                  type="button"
                  onClick={() => navigate(`/case/${item.matter_id}`)}
                  style={{
                    border: '1px solid var(--border-default)',
                    background: 'var(--bg-base)',
                    borderRadius: '18px',
                    padding: '16px',
                    textAlign: 'left',
                    display: 'grid',
                    gap: '8px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 800 }}>{item.title}</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 700 }}>
                      {item.history_meta || item.origin || 'Matter'}
                    </span>
                  </div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {item.summary || 'No matter summary yet.'}
                  </span>
                </button>
              ))
            ) : (
              <AppEmptyState
                icon={Scale}
                title="No saved matters yet"
                description="Create a matter first, then judge strategy can stay attached to that file instead of becoming a separate product area."
              />
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: '14px', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
              <label style={{ display: 'grid', gap: '8px' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Matter title</span>
                <input className="advoq-input" value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} placeholder="Example: Interim injunction in trademark dispute" />
              </label>
              <label style={{ display: 'grid', gap: '8px' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Court or forum</span>
                <input className="advoq-input" value={form.court_forum} onChange={(event) => setForm((prev) => ({ ...prev, court_forum: event.target.value }))} placeholder="Delhi High Court, NCLT, trial court..." />
              </label>
              <label style={{ display: 'grid', gap: '8px' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Case number</span>
                <input className="advoq-input" value={form.case_number} onChange={(event) => setForm((prev) => ({ ...prev, case_number: event.target.value }))} placeholder="Optional" />
              </label>
            </div>

            {mode === 'indiankanoon' ? (
              <div style={{ display: 'grid', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    className="advoq-input"
                    style={{ flex: 1, minWidth: '240px' }}
                    value={form.source_query}
                    onChange={(event) => setForm((prev) => ({ ...prev, source_query: event.target.value }))}
                    placeholder="Search Indian Kanoon by keyword"
                  />
                  <button type="button" className="advoq-btn-primary" onClick={runIndianKanoonSearch} disabled={searchingIK}>
                    {searchingIK ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                    Search
                  </button>
                </div>
                {ikResults.length ? (
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {ikResults.map((result) => (
                      <button
                        key={result.tid}
                        type="button"
                        onClick={() => selectImportedCase(result)}
                        style={{
                          border: selectedImportedCase?.tid === result.tid ? '1px solid rgba(15,118,110,0.38)' : '1px solid var(--border-default)',
                          background: 'var(--bg-base)',
                          borderRadius: '18px',
                          padding: '16px',
                          textAlign: 'left',
                          display: 'grid',
                          gap: '8px',
                        }}
                      >
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 800 }}>{result.title}</span>
                          <ProvenanceChip item={result.provenance?.[0] || { label: 'Verified Indian Kanoon', verified: true }} />
                        </div>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                          {trimText(result.headline || result.docsource || 'Verified authority result', 260)}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            <label style={{ display: 'grid', gap: '8px' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Matter summary</span>
              <textarea className="advoq-input" rows={4} value={form.summary} onChange={(event) => setForm((prev) => ({ ...prev, summary: event.target.value }))} placeholder="Facts, posture, relief, and what you want Advoq to help with." />
            </label>
            <label style={{ display: 'grid', gap: '8px' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Current objective</span>
              <textarea className="advoq-input" rows={3} value={form.current_objective} onChange={(event) => setForm((prev) => ({ ...prev, current_objective: event.target.value }))} placeholder="What should stay active as the matter moves into overview, draft, files, Jolly, or review?" />
            </label>

            {selectedImportedCase?.judgeInference?.name ? (
              <div style={{ borderRadius: '18px', border: '1px solid var(--border-default)', background: 'var(--bg-base)', padding: '14px', display: 'grid', gap: '6px' }}>
                <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                  Potential judge context detected: {selectedImportedCase.judgeInference.name}
                </span>
                <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                  Save the matter first, then open bench prep from the matter overview so it stays optional and attached to the live matter.
                </span>
              </div>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <JollyDictationButton label="Jolly intake" onAppendText={(text) => setForm((prev) => ({ ...prev, summary: `${prev.summary}${prev.summary ? ' ' : ''}${text}` }))} />
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button type="button" className="app-chat-button" onClick={() => navigate('/jolly')}>
                  <Mic size={13} /> Open Jolly
                </button>
                <button type="button" className="advoq-btn-primary" onClick={() => saveMatter({ navigateToMatter: true })} disabled={savingMatter}>
                  {savingMatter ? <Loader2 size={14} className="animate-spin" /> : <Scale size={14} />}
                  Create matter
                </button>
              </div>
            </div>
          </>
        )}
      </section>
    </>
  );

  const renderOverview = () => {
    const matter = matterWorkspace?.matter || {};
    const caseRecord = matterWorkspace?.case || {};
    const hearingPack = matterWorkspace?.hearingPack || {};
    const judgeQuotaText = `${judgeProfiles.length}/${judgeLimitLabel(judgeLimit)} active judge agents`;
    const matterTitle = matter.title || matter.name || matter.matter_id || 'Matter';
    const overviewRoute = getMatterDeskRoute(matterDesk, 'sabha', `/war-room/${matter.matter_id}`);
    const draftRoute = withQueryParams('/documents', { matter_id: matter.matter_id, matter_name: matterTitle });
    const filesRoute = withQueryParams(getMatterDeskRoute(matterDesk, 'research', `/research?matter_id=${encodeURIComponent(matter.matter_id)}`), {
      matter_id: matter.matter_id,
      matter_name: matterTitle,
      context_panel: 'files',
    });
    const jollyRoute = withQueryParams(getMatterDeskRoute(matterDesk, 'jolly', `/jolly?matter_id=${encodeURIComponent(matter.matter_id)}`), {
      matter_id: matter.matter_id,
      matter_name: matterTitle,
    });
    const reviewRoute = getMatterDeskRoute(matterDesk, 'agents', `/war-room/${matter.matter_id}?view=agents`);
    const metrics = [
      { icon: Users, label: 'Matter desk', value: matterDesk?.readiness?.label || 'Building', note: matterDesk?.readiness?.next_focus || 'One matter view stays central for this file.' },
      { icon: Scale, label: 'Authorities', value: (matterWorkspace?.authorities || []).length, note: 'Verified Indian sources in the matter trail' },
      { icon: FileStack, label: 'Matter files', value: (matterWorkspace?.documents || []).length, note: 'Uploads and file-linked context' },
      { icon: Mic, label: 'Jolly sessions', value: (matterWorkspace?.jollySessions || []).length, note: 'Jolly linked to this matter' },
    ];

    return (
      <>
        <MatterWorkspaceContext
          matterName={matterTitle}
          matterId={matter.matter_id}
          workspaceId={caseRecord.workspace_id || matterDesk.workspace_id}
          objective={matterDesk?.current_objective?.text || 'Continue this matter without switching mental models.'}
          items={[
            { key: 'overview', onClick: () => navigate(overviewRoute) },
            { key: 'draft', onClick: () => navigate(draftRoute) },
            { key: 'files', onClick: () => navigate(filesRoute) },
            { key: 'jolly', onClick: () => navigate(jollyRoute) },
            { key: 'review', onClick: () => navigate(reviewRoute) },
          ]}
          activeKey="overview"
          compact
          caption="Matter context"
          testId="matter-workspace-context"
        />

        <section style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          {metrics.map((item) => (
            <AppMetricCard key={item.label} icon={item.icon} label={item.label} value={item.value} note={item.note} />
          ))}
        </section>

        <section style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 360px)' }}>
          <div style={{ display: 'grid', gap: '16px' }}>
            <HearingPrepWorkspace
              hearingPack={hearingPack}
              activeMode={hearingMode}
              onChangeMode={setHearingMode}
              onRegenerate={regenerateHearingPack}
              regenerating={hearingPackRefreshing}
            />
          </div>

          <div className="advoq-card" style={{ padding: '22px', borderRadius: '24px', display: 'grid', gap: '18px', alignSelf: 'start' }}>
            <AppSectionHeader
              eyebrow="Matter continuity"
              title="Continue from one matter"
              description="Keep hearing prep, files, Jolly, and review tied to the same live objective."
            />
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {(caseRecord.provenance || []).map((item, index) => (
                <ProvenanceChip key={`${item.label}-${index}`} item={item} />
              ))}
            </div>
            <div style={{ display: 'grid', gap: '8px' }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Current objective</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                {matterDesk?.current_objective?.text || 'Needs review'}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Matter summary</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                {caseRecord.summary || matter.description || 'No matter summary yet.'}
              </div>
            </div>
            {(matterDesk?.selected_context || []).length ? (
              <div style={{ display: 'grid', gap: '8px' }}>
                <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Context carried forward</div>
                {(matterDesk.selected_context || []).map((item) => (
                  <div key={`${item.type}-${item.value}`} style={{ border: '1px solid var(--border-default)', borderRadius: '14px', padding: '12px 14px', background: 'var(--bg-base)' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
                      {item.label}
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', lineHeight: 1.6 }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" className="advoq-btn-primary" onClick={() => navigate(overviewRoute)}>
                <Users size={13} /> Continue
              </button>
              <button type="button" className="app-chat-button" onClick={() => navigate(filesRoute)}>
                <Search size={13} /> Files
              </button>
              <button type="button" className="app-chat-button" onClick={() => navigate(reviewRoute)}>
                <Sparkles size={13} /> Review
              </button>
            </div>
          </div>
        </section>

        <section className="advoq-card" style={{ padding: '22px', borderRadius: '24px', display: 'grid', gap: '18px' }}>
          <AppSectionHeader
            eyebrow="Judge strategy"
            title="Bench prep stays in the matter flow"
            description="Open bench prep only when the file needs it, without turning setup into a separate destination."
            actions={(
              <button
                type="button"
                className="advoq-btn-primary"
                onClick={() => navigate(getMatterDeskRoute(matterDesk, 'bench', `/war-room/${matter.matter_id}?view=bench`))}
              >
                <Users size={13} /> Open judge strategy
              </button>
            )}
          />
          <div style={{ display: 'grid', gap: '14px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <div style={{ borderRadius: '18px', border: '1px solid var(--border-default)', background: 'var(--bg-base)', padding: '16px', display: 'grid', gap: '8px' }}>
              <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>What moved</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                Search a real judge, infer context from matter files, create simulated judge agents, and continue bench-prep chat directly inside the matter workspace.
              </div>
            </div>
            <div style={{ borderRadius: '18px', border: '1px solid var(--border-default)', background: 'var(--bg-base)', padding: '16px', display: 'grid', gap: '8px' }}>
              <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>Current matter status</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                {judgeProfiles.length
                  ? `${judgeQuotaText}. Existing judge profiles stay attached to this matter and open from the overview.`
                  : 'No judge agents attached yet. You can add them later from the overview without cluttering intake.'}
              </div>
            </div>
            <div style={{ borderRadius: '18px', border: '1px solid var(--border-default)', background: 'var(--bg-base)', padding: '16px', display: 'grid', gap: '8px' }}>
              <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>Trust and restraint</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                Sabha synthesis keeps the distinction clear between real judge-linked analysis, matter-linked context, and simulated agents. The feature stays strategic, not predictive.
              </div>
            </div>
          </div>
        </section>
      </>
    );
  };

  return (
    <main className="app-main" data-testid="case-page">
      <AppPage width="wide">
        <AppPageHeader
          eyebrow={matterWorkspace ? 'Matter' : 'Matter intake'}
          title={matterWorkspace?.case?.title || 'Open a matter'}
          description={matterWorkspace
            ? 'Keep intake and hearing-pack continuity attached to the matter.'
            : 'Open the matter first. Bench prep becomes available only after the file exists.'}
          actions={(
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <FeatureHistoryMenu
                feature="case"
                label="Recent matters"
                activeId={matterWorkspace?.matter?.matter_id || ''}
                onSelect={openRecentCase}
              />
              {matterWorkspace ? (
                <>
                  <button
                    type="button"
                    className="app-chat-button"
                    onClick={() => navigate(getMatterDeskRoute(matterDesk, 'bench', `/war-room/${matterWorkspace.matter?.matter_id}?view=bench`))}
                  >
                    <Users size={13} /> Judge strategy
                  </button>
                  <button type="button" className="app-chat-button" onClick={() => saveMatter()} disabled={savingMatter}>
                    <Scale size={13} /> Save
                  </button>
                  <button type="button" className="app-chat-button" onClick={() => navigate('/case')}>
                    <Scale size={13} /> New matter
                  </button>
                </>
              ) : null}
            </div>
          )}
          meta={matterWorkspace ? (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Matter ID: {matterWorkspace.matter?.matter_id}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Workspace ID: {matterWorkspace.case?.workspace_id || 'Pending'}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Judge agents: {judgeProfiles.length}</span>
            </div>
          ) : null}
        />

        {loadingMatter ? (
          <div style={{ color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <Loader2 size={14} className="animate-spin" /> Loading matter strategy...
          </div>
        ) : matterWorkspace ? renderOverview() : renderIntake()}
      </AppPage>
    </main>
  );
}

export default CasePage;
